import { Link2, Loader2 } from 'lucide-react'
import type { ViaShortcutLink } from '../client/index.js'

/**
 * A folder's Links tab: the share links that reach it THROUGH A SHORTCUT (a link on another
 * folder that holds a `.chain` into this one — owner ruling: listed here, no new tab). Only
 * what the server sends — the link's kind, who made it, when, and the shortcut's name where
 * the viewer may see it — never its token, URL or root, nor the shortcut's path. Revoke is the
 * link's revoke, offered only where the server says the viewer manages the folder the link
 * ENTERS through (`canRevoke`).
 *
 * Drawn like an inherited link (`AllLinksView`): a "Via <shortcut>" label with an L connector
 * into the card. The server names a shortcut only to a viewer who can open it in their own
 * drive (it can sit in a private app folder); otherwise the label reads "Via a shortcut".
 */
/** "Via 🔗 oo.chain" (+N for more), or "Via a shortcut" when none is named for this viewer. */
function ViaLabel({ shortcuts }: { shortcuts: Array<{ id: string; name: string }> }) {
  const [first, ...rest] = shortcuts
  return (
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide themed-text-muted pl-1 min-w-0">
      {first ? (
        <>
          <span className="shrink-0">Via</span>
          <Link2 size={11} className="shrink-0" />
          <span className="truncate normal-case text-[11px] font-medium" title={shortcuts.map((s) => s.name).join(', ')}>
            {first.name}
          </span>
          {rest.length > 0 && <span className="shrink-0 normal-case text-[11px]">{`+${rest.length}`}</span>}
        </>
      ) : (
        <>
          <span className="shrink-0">Via a shortcut</span>
          <Link2 size={11} className="shrink-0" />
        </>
      )}
    </div>
  )
}

export function ViaShortcutSection({ links, incomplete = false, busyId, onRevoke }: {
  links: ViaShortcutLink[]
  /** The server's bounded search couldn't check every such link. */
  incomplete?: boolean
  busyId: string | null
  onRevoke: (link: ViaShortcutLink) => void
}) {
  if (links.length === 0 && !incomplete) return null
  return (
    <div className="mt-2 space-y-2">
      {incomplete && (
        <div className="text-[11px] themed-text-muted pl-1">
          Links that reach here through a shortcut may not all be listed — there were too many to check.
        </div>
      )}
      {links.map((l) => (
        <div key={l.id}>
          <ViaLabel shortcuts={l.shortcuts ?? []} />
          <div className="flex items-stretch mt-0.5">
            <div className="relative w-4 shrink-0" aria-hidden>
              <div className="absolute left-[7px] right-0 -top-0.5 bottom-1/2 border-l-2 border-b-2 rounded-bl-lg themed-border" />
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-3 rounded-md border themed-border px-3 py-2">
              <div className="flex-1 min-w-0">
                {/* A loose-HTML app link can be listed too (never a PDApp link — its grants show on the PDApp tab). */}
                <div className="text-sm themed-text truncate">{l.kind === 'app' ? 'App link' : 'Share link'}</div>
                <div className="text-[11px] themed-text-muted truncate">
                  {`Made by ${l.creator ? (l.creator.name || l.creator.email) : 'someone no longer here'} · ${new Date(l.createdAt).toLocaleDateString()}`}
                </div>
              </div>
              {l.canRevoke && <button
                type="button"
                onClick={() => onRevoke(l)}
                disabled={busyId === l.id}
                className="themed-btn-secondary inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium rounded-md disabled:opacity-50 shrink-0"
                title="Stop this link working — for everyone it was shared with"
              >
                {busyId === l.id && <Loader2 size={11} className="animate-spin" />}
                Revoke
              </button>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
